import axios from 'axios';
import { hasInternet, isConnectivityLost, logger, normalizeApiError } from '@clarix/utils';
import {
  NETWORK_MESSAGE,
  S3_PUT_IDLE_TIMEOUT_MS,
  S3_UNREACHABLE_AFTER,
  UPLOAD_UNREACHABLE_MESSAGE,
} from './constants';

/**
 * A direct browser→S3 part upload failed. `network` is true when nothing answered (offline, no
 * response, a timeout): the caller waits and retries rather than failing the recording.
 * `unreachable`: S3 kept not answering while the internet works, so it is no longer "offline".
 */
export class S3UploadError extends Error {
  constructor(
    message: string,
    public override readonly cause?: unknown,
    public readonly network = false,
    public readonly unreachable = false,
  ) {
    super(message);
    this.name = 'S3UploadError';
  }
}

const S3_ETAG_MISSING_MESSAGE =
  'The audio uploaded but storage did not return a confirmation (ETag). ' +
  'The storage (S3) CORS configuration must expose the ETag response header. ' +
  'Please contact your administrator.';

/** No-response PUTs in a row while the internet answered; any success resets it. */
let unreachableStreak = 0;

/** S3 itself keeps not answering though the internet works: retried, but not shown as offline. */
export function isStorageUnreachable(err: unknown): boolean {
  return err instanceof S3UploadError && err.unreachable;
}

/** No answer from the network (offline, no response, a timeout): a wait, never a failure of the take. */
export function isNetworkFailure(err: unknown): boolean {
  if (isConnectivityLost()) return true;
  if (err instanceof S3UploadError) return err.network;
  const e = normalizeApiError(err);
  return e.isNetwork || e.isTimeout || e.isOffline;
}

/**
 * PUTs one part to its presigned URL (no Content-Type, no auth) and returns the ETag. Aborted after
 * S3_PUT_IDLE_TIMEOUT_MS without progress, so a hung connection reaches the caller's retry while a
 * slow, moving upload is never cut off. No response is a network wait; an empty ETag is a CORS gap.
 */
export async function uploadChunkToS3(presignedUrl: string, chunk: Blob): Promise<string> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = () => {
    clearTimeout(timer);
    timer = setTimeout(() => controller.abort(), S3_PUT_IDLE_TIMEOUT_MS);
  };
  let response;
  try {
    arm();
    response = await axios.put(presignedUrl, chunk, { signal: controller.signal, onUploadProgress: arm });
  } catch (err) {
    if (controller.signal.aborted || isConnectivityLost() || (axios.isAxiosError(err) && !err.response)) {
      // A missing bucket CORS rule looks the same from here; the log keeps that hint for support.
      logger.warn('[Recording] S3 PUT got no response (network, timeout, or bucket CORS)', err);
      // The probe also feeds the shared connectivity store, whose recovery wakes the upload queue.
      const internet = !isConnectivityLost() && (await hasInternet());
      unreachableStreak = internet ? unreachableStreak + 1 : 0;
      const unreachable = unreachableStreak >= S3_UNREACHABLE_AFTER;
      throw new S3UploadError(unreachable ? UPLOAD_UNREACHABLE_MESSAGE : NETWORK_MESSAGE, err, true, unreachable);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }

  unreachableStreak = 0;
  const etag = response.headers['etag'] ?? response.headers['ETag'] ?? '';
  if (!etag) {
    // PUT succeeded but the ETag was stripped — bucket CORS is not exposing it.
    throw new S3UploadError(S3_ETAG_MISSING_MESSAGE);
  }
  return etag;
}
